/**
 * Copyright 2020 (c) Dominik Basner, Sotec GmbH (for VDW e.V.)
 * Copyright 2020 (c) Fabian Wilhelm, Sotec GmbH (for VDW e.V.)
 * Copyright 2020-2024 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021-2022 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 * Copyright 2025 (c) Nico Brandt, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import ErrorBoundary from 'components/detail/common/ErrorBoundary';
import React, { useEffect, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { IStore } from 'redux/storeType';
import ENVs from 'utilities/ENVs';
import { IsLandscape } from 'utilities/Orientation';
import OpcUaMqttPubSub from 'utilities/PubSub/OpcUaMqttPubSub';
import {
  getFair,
  getFairs,
  getMachine,
  getMachines,
  getMachinesByFair,
  staticBucketReachable,
  watermarkedBucketReachable,
} from '../../redux/selectors';
import TvViewDetailPubSub from './TvViewDetailPubSub';
import TvViewDetailPubSubVerbose from './TvViewDetailPubSubVerbose';
import TvViewDetail from './tvViewDetail';
import TvViewMachineGrid from './tvViewMachineGrid';
import TvViewMapList from './tvViewMapList';
import MachineHandling from 'utilities/MachineHandling';

const SlideTypes = {
  MapList: 0,
  MachineGrid: 1,
  MachineDetail: 2,
  ImgLogo: 3,
  ImgNumbers: 4,
  ImgApp: 5,
  Video: 6,
  ImgDaily: 7,
};

function MapListWrapper(props) {
  // TODO filter necessary properties
  return <TvViewMapList {...props} />;
}

function MachineGridWrapper(props) {
  return <TvViewMachineGrid machines={props.machinesOnFair} />;
}

function MachineDetailWrapper(props) {
  if (props.machinesOnFair.length) {
    const machine = props.match.params.machine
      ? props.machine
      : props.machinesOnFair[props.subIteration % props.machinesOnFair.length];
    const encoding = MachineHandling.getEncodingVersion(machine);

    if (encoding === 3) {
      return (
        <OpcUaMqttPubSub topicPath={machine.MqttTopic || ''}>
          <TvViewDetailPubSub machine={machine} params={props.match.params} />
        </OpcUaMqttPubSub>
      );
    } else if (encoding === 4) {
      return (
        <OpcUaMqttPubSub topicPath={machine.MqttTopic || ''}>
          <TvViewDetailPubSubVerbose machine={machine} params={props.match.params} />
        </OpcUaMqttPubSub>
      );
    } else if (encoding === 2) {
      return <TvViewDetail {...props} machine={machine} />;
    } else {
      return <div>Unknown encoding {encoding}</div>;
    }
  }
  return <p>No Machines on the fair.</p>;
}

function ImgSelector({ landscape, portrait, alt }: { landscape: string; portrait: string; alt: string }) {
  const [isLandscape, setIsLandscape] = useState<boolean>(IsLandscape());
  useEffect(() => {
    const handOrientationChange = () => {
      setIsLandscape(IsLandscape());
    };
    window.addEventListener('resize', handOrientationChange);
    return () => {
      window.removeEventListener('resize', handOrientationChange);
    };
  }, []);

  if (isLandscape) {
    return <img alt={alt} className="fullscreen" src={landscape} />;
  } else {
    return <img alt={alt} className="fullscreen" src={portrait} />;
  }
}

function ImgLogoWrapper(props) {
  const landscapeImg = `${ENVs.WatermarkedBucketUrl(props.watermarkedBucketReachable)}/watermarked/TvView/${props.match.params.fair}/1_logo_HD.jpg`;
  const portraitImg = `${ENVs.WatermarkedBucketUrl(props.watermarkedBucketReachable)}//watermarked/TvView/${props.match.params.fair}/1_logo_HD_P.jpg`;
  return <ImgSelector alt="showcase logo" landscape={landscapeImg} portrait={portraitImg} />;
}

function ImgNumbersWrapper(props) {
  const landscapeImg = `${ENVs.WatermarkedBucketUrl(props.watermarkedBucketReachable)}/watermarked/TvView/${props.match.params.fair}/2_zahlen_HD.jpg`;
  const portraitImg = `${ENVs.WatermarkedBucketUrl(props.watermarkedBucketReachable)}/watermarked/TvView/${props.match.params.fair}/2_zahlen_HD_P.jpg`;
  return <ImgSelector alt="showcase numbers" landscape={landscapeImg} portrait={portraitImg} />;
}

function ImgAppWrapper(props) {
  const landscapeImg = `${ENVs.WatermarkedBucketUrl(props.watermarkedBucketReachable)}/watermarked/TvView/${props.match.params.fair}/3_app_HD.jpg`;
  const portraitImg = `${ENVs.WatermarkedBucketUrl(props.watermarkedBucketReachable)}/watermarked/TvView/${props.match.params.fair}/3_app_HD_P.jpg`;
  return <ImgSelector alt="showcase app" landscape={landscapeImg} portrait={portraitImg} />;
}

function VideoWrapper(props) {
  /// Chrome does not allow autoplay without muted

  const videoRef = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Auto-start only when video is visible
    if (props.isVisible) {
      video.play().catch((err) => {
        console.error(err);
      });
    } else {
      video.pause();
    }
  }, [props.isVisible]);

  const videoSrc = `${ENVs.WatermarkedBucketUrl(props.watermarkedBucketReachable)}/watermarked/TvView/${props.match.params.fair}/umati.webm`;

  return (
    <video
      ref={videoRef}
      src={videoSrc}
      className="fullscreen fullscreen-width"
      preload="auto" // Browser should load the video immediately (even if not yet playing)
      controls={false} // Do not display controls (play, pause, volume)
      muted={true} // always muted
      autoPlay // Start playback automatically
      playsInline // On mobile devices, the video shall be played inline, not in full screen
    />
  );
}

function ImgDailyWrapper(props) {
  const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const weekday = weekdays[new Date().getDay()];
  const landscapeImg = `${ENVs.WatermarkedBucketUrl(props.watermarkedBucketReachable)}/watermarked/TvView/${props.match.params.fair}/today_${weekday}.jpg`;
  const portraitImg = `${ENVs.WatermarkedBucketUrl(props.watermarkedBucketReachable)}/watermarked/TvView/${props.match.params.fair}/today_${weekday}_P.jpg`;
  return <ImgSelector alt="showcase daily" landscape={landscapeImg} portrait={portraitImg} />;
}

const slides = [
  MapListWrapper,
  MachineGridWrapper,
  MachineDetailWrapper,
  ImgLogoWrapper,
  ImgNumbersWrapper,
  ImgAppWrapper,
  VideoWrapper,
  ImgDailyWrapper,
];

const slideOrder = [
  { slideType: SlideTypes.MapList, durationInSec: 8 },
  { slideType: SlideTypes.MachineGrid, durationInSec: 8 },
  { slideType: SlideTypes.MachineDetail, durationInSec: 8 },
  { slideType: SlideTypes.MachineDetail, durationInSec: 8 },
  { slideType: SlideTypes.MachineDetail, durationInSec: 8 },
  { slideType: SlideTypes.MachineDetail, durationInSec: 8 },
  { slideType: SlideTypes.ImgLogo, durationInSec: 8 },
  { slideType: SlideTypes.ImgNumbers, durationInSec: 8 },
  { slideType: SlideTypes.ImgApp, durationInSec: 8 },
  { slideType: SlideTypes.Video, durationInSec: 7 },
  { slideType: SlideTypes.ImgDaily, durationInSec: 8 },
];

class StateCalculator {
  numPerElement: { [key: string]: number };
  offset: number[];
  subOffset: number[];
  totalOffset: number;

  constructor() {
    this.numPerElement = {};
    this.offset = [];
    this.subOffset = [];
    this.totalOffset = 0;
    for (const el of slideOrder) {
      if (!this.numPerElement[el.slideType]) {
        this.numPerElement[el.slideType] = 0;
      }
      this.subOffset.push(this.numPerElement[el.slideType]);
      ++this.numPerElement[el.slideType];
      this.totalOffset += el.durationInSec;
      this.offset.push(this.totalOffset);
    }
  }

  GetState(num: number) {
    num = Math.floor(num);
    const bounded = num % this.totalOffset;
    let iSlide = -1;
    for (const [iOffset, offset] of this.offset.entries()) {
      if (bounded < offset) {
        iSlide = iOffset;
        break;
      }
    }
    if (iSlide === -1) {
      /// Should never happen
      throw new Error('Corrupted data structures.');
    }
    const slideDefinition = slideOrder[iSlide];
    const iteration = Math.floor(num / this.totalOffset);
    const subIteration = iteration * this.numPerElement[slideDefinition.slideType] + this.subOffset[iSlide];

    return {
      slideType: slideDefinition.slideType,
      subIteration,
      slideIndex: iSlide,
    };
  }

  GetStateFromTime() {
    const number = Math.floor(new Date().getTime() / 1000);
    return this.GetState(number);
  }
}

export type ITvViewParams = {
  fair: string;
  slide?: string;
  machine?: string;
};

const stateCalculator = new StateCalculator();

// FixedSlot: Container to mount a specific slide component instance
function FixedSlot({
  slideType,
  subIteration,
  match,
  props,
}: {
  slideType: number;
  subIteration: number;
  match: { params: ITvViewParams };
  props: Record<string, unknown>;
}) {
  const SlideComp = slides[slideType];
  return <SlideComp match={match} {...props} subIteration={subIteration} />;
}

export function TvView(prop: { watermarkedBucketReachableValue: boolean; staticBucketReachableValue: boolean }) {
  const params = useParams() as ITvViewParams;

  const props = {
    machines: useSelector((s: IStore) => getMachines(s)),
    machinesOnFair: useSelector((s: IStore) => getMachinesByFair(s, params.fair)),
    fairs: useSelector((s: IStore) => getFairs(s)),
    fair: useSelector((s: IStore) => getFair(s, params.fair)),
    machine: useSelector((s: IStore) => getMachine(s, params.machine)),
    watermarkedBucketReachable: prop.watermarkedBucketReachableValue,
    staticBucketReachable: prop.staticBucketReachableValue,
    isVisible: false,
  };

  // current shown state and time-driven next state
  const [currentState, setCurrentState] = useState(stateCalculator.GetStateFromTime());
  const [nextState, setNextState] = useState(currentState);

  // Refs
  const startTimestampRef = useRef(Date.now());
  const isPreloadedRef = useRef(false);

  // which slot is visible
  const [visibleSlot, setVisibleSlot] = useState<0 | 1>(0);
  const hiddenSlot = visibleSlot === 0 ? 1 : 0;

  // per-slot mounted content
  const [slot0State, setSlot0State] = useState(currentState);
  const [slot1State, setSlot1State] = useState(currentState);

  // if slide is forced via URL parameter, show only that slide
  if (params.slide) {
    const SlideOverride = slides[parseInt(params.slide) % slides.length];

    return (
      <div className="tv-wrapper">
        <div className="tv-slide">
          <ErrorBoundary state={`${params.slide}-${currentState.subIteration}`}>
            <SlideOverride match={{ params }} {...props} subIteration={currentState.subIteration} />
          </ErrorBoundary>
        </div>
      </div>
    );
  } else {
    // slideshow mode

    // Linter disabled since the timer usage is intended
    // timer: compute nextState every 250ms
    useEffect(() => {
      const timer = setInterval(() => {
        setNextState(stateCalculator.GetStateFromTime());
      }, 250);
      return () => clearInterval(timer);
    }, []);

    // when nextState changes, preload it into hidden slot
    // There seem to be a drift in preloading (Firefox when not focused). Browser refreshing solves it for now.
    // Probably change the visibility switch to time driven instead of state driven.
    useEffect(() => {
      const slideChanged =
        nextState.slideType !== currentState.slideType || nextState.subIteration !== currentState.subIteration;

      if (!slideChanged) return;

      // load nextState into hidden slot
      if (!isPreloadedRef.current) {
        if (hiddenSlot === 0) {
          setSlot0State(nextState);
        } else {
          setSlot1State(nextState);
        }
      }

      isPreloadedRef.current = true;
    }, [nextState]);

    // when currentState changes, check if it is time to switch visibility
    useEffect(() => {
      if (!isPreloadedRef.current) return;

      const currentSlide = slideOrder[currentState.slideIndex];
      const durationInMs = currentSlide.durationInSec * 1000;

      if (Date.now() >= startTimestampRef.current + durationInMs) {
        setVisibleSlot(hiddenSlot);
        setCurrentState(nextState);
        startTimestampRef.current = Date.now();
        isPreloadedRef.current = false;
      }
    }, [currentState, nextState, hiddenSlot]);

    return (
      <div className="tv-wrapper" style={{ display: 'flex' }}>
        {/* SLOT 0 */}
        <div className="tv-slide" style={{ flex: 1, display: visibleSlot === 0 ? 'block' : 'none' }}>
          <ErrorBoundary state={`slot0-${slot0State.slideType}-${slot0State.subIteration}`}>
            <FixedSlot
              key={`slot0-${slot0State.slideType}-${slot0State.subIteration}`}
              slideType={slot0State.slideType}
              subIteration={slot0State.subIteration}
              match={{ params }}
              props={{ ...props, isVisible: visibleSlot === 0 }}
            />
          </ErrorBoundary>
        </div>

        {/* SLOT 1 */}
        <div className="tv-slide" style={{ flex: 1, display: visibleSlot === 1 ? 'block' : 'none' }}>
          <ErrorBoundary state={`slot1-${slot1State.slideType}-${slot1State.subIteration}`}>
            <FixedSlot
              key={`slot1-${slot1State.slideType}-${slot1State.subIteration}`}
              slideType={slot1State.slideType}
              subIteration={slot1State.subIteration}
              match={{ params }}
              props={{ ...props, isVisible: visibleSlot === 1 }}
            />
          </ErrorBoundary>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: IStore) => {
  return {
    watermarkedBucketReachableValue: watermarkedBucketReachable(state) || false,
    staticBucketReachableValue: staticBucketReachable(state) || false,
  };
};

export default connect(mapStateToProps)(TvView);
